// Mall category helpers — the tree is owned by the gateway (gateway.db).
// The API returns a flat list; these helpers group it for the SPA.

import type { MallCategory } from './api.js';

/** Root-category icons. Falls back to a neutral shopping glyph. */
const CATEGORY_ICONS: Record<string, string> = {
  supplement: '💊',
  supplements: '💊',
  medicines: '🩹',
  'medical-supplies': '🩺',
  'food-beverage': '🍵',
  'mom-baby': '🍼',
  'skin-care': '🧴',
  'personal-care': '🧼',
  'mens-care': '🧔',
  uncategorised: '📦',
};

export function categoryIcon(category: MallCategory): string {
  return CATEGORY_ICONS[category.id] || CATEGORY_ICONS[category.slug] || '🛒';
}

/** Root categories in server sort order (system bucket last). */
export function rootCategories(categories: MallCategory[]): MallCategory[] {
  return categories
    .filter((c) => !c.parentId)
    .sort((a, b) => {
      if (!!a.system !== !!b.system) return a.system ? 1 : -1;
      return (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name);
    });
}

export function childrenOf(categories: MallCategory[], parentId: string): MallCategory[] {
  return categories
    .filter((c) => c.parentId === parentId)
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name));
}

export function findCategoryBySlug(categories: MallCategory[], slug: string): MallCategory | undefined {
  return categories.find((c) => c.slug === slug) ?? categories.find((c) => c.id === slug);
}

export function findCategoryById(categories: MallCategory[], id: string): MallCategory | undefined {
  return categories.find((c) => c.id === id);
}

/** Root + leaf path for breadcrumbs (empty when the leaf is unknown). */
export function categoryPath(categories: MallCategory[], leafId: string): MallCategory[] {
  const leaf = findCategoryById(categories, leafId);
  if (!leaf) return [];
  if (!leaf.parentId) return [leaf];
  const root = findCategoryById(categories, leaf.parentId);
  return root ? [root, leaf] : [leaf];
}

/** Deep link for a category page. */
export function categoryHref(category: MallCategory, storeCode?: string): string {
  const base = `/c/${encodeURIComponent(category.slug)}`;
  return storeCode ? `${base}?store=${encodeURIComponent(storeCode)}` : base;
}
